import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ProductId } from '../../common/ui/app-tile/app-selector/types';

import { useAppTilesState, useFocusSelectedTile, useMoveSelectedTile } from './hooks';
import type { KeyboardReorderContextValue, KeyboardReorderProviderProps } from './types';

export const KeyboardReorderContext: React.Context<KeyboardReorderContextValue | null> =
	createContext<KeyboardReorderContextValue | null>(null);

export function KeyboardReorderProvider({
	children,
}: KeyboardReorderProviderProps): React.JSX.Element {
	const [isEnabled, setIsEnabled] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState<ProductId | null>(null);

	const { appTiles, setAppTiles, temporaryOrder, setTemporaryOrder, onReorder, setOnReorder } =
		useAppTilesState();

	const tileRefsMap = useRef<Map<ProductId, HTMLAnchorElement>>(new Map());
	const toggleRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const focusSelectedTile = useFocusSelectedTile(selectedProductId, tileRefsMap);
	const moveSelectedTile = useMoveSelectedTile(
		temporaryOrder,
		setTemporaryOrder,
		selectedProductId,
		focusSelectedTile,
	);

	useEffect(() => {
		if (!isEnabled || !isVisible) {
			setSelectedProductId(null);
		}
	}, [isVisible, isEnabled, setSelectedProductId]);

	useEffect(() => {
		if (!isEnabled) {
			setTemporaryOrder(appTiles);
		}
	}, [isEnabled, appTiles, setTemporaryOrder]);

	const cancelReorder = useCallback(() => {
		if (selectedProductId) {
			setSelectedProductId(null);
			setTemporaryOrder(appTiles);
		} else {
			toggleRef.current?.focus();
		}
	}, [appTiles, selectedProductId, setSelectedProductId, setTemporaryOrder, toggleRef]);

	const applyReorder = useCallback(() => {
		onReorder?.(temporaryOrder);
		focusSelectedTile();
		setSelectedProductId(null);
	}, [onReorder, temporaryOrder, focusSelectedTile, setSelectedProductId]);

	const exitReorderMode = useCallback(() => {
		setIsVisible(false);
		setIsEnabled(false);
		setSelectedProductId(null);
		setTemporaryOrder(appTiles);
	}, [appTiles, setIsVisible, setIsEnabled, setSelectedProductId, setTemporaryOrder]);

	const contextValue = useMemo<KeyboardReorderContextValue>(
		() => ({
			isKeyboardReorderToggleVisible: isVisible,
			isKeyboardReorderModeEnabled: isEnabled,
			selectedKeyboardReorderProductId: selectedProductId,
			setIsKeyboardReorderToggleVisible: setIsVisible,
			setIsKeyboardReorderModeEnabled: setIsEnabled,
			setKeyboardSelectedProductId: setSelectedProductId,
			temporaryKeyboardOrderedAppTiles: temporaryOrder,
			setAppTiles,
			setOnAppsReorder: setOnReorder,
			moveKeyboardReorderSelectedTile: moveSelectedTile,
			keyboardReorderAppTileRefsMap: tileRefsMap,
			keyboardReorderToggleRef: toggleRef,
			cancelKeyboardReorder: cancelReorder,
			applyKeyboardReorder: applyReorder,
			exitKeyboardReorderMode: exitReorderMode,
			containerRef,
		}),
		[
			isVisible,
			isEnabled,
			selectedProductId,
			setIsVisible,
			setIsEnabled,
			setSelectedProductId,
			temporaryOrder,
			setAppTiles,
			setOnReorder,
			moveSelectedTile,
			tileRefsMap,
			toggleRef,
			cancelReorder,
			applyReorder,
			exitReorderMode,
			containerRef,
		],
	);

	return (
		<KeyboardReorderContext.Provider value={contextValue}>
			{children}
		</KeyboardReorderContext.Provider>
	);
}

export { useKeyboardReorderContext } from './hooks';
